/**
 * Subscription Store
 *
 * Zustand store for managing subscription state.
 * Provides feature access checking and subscription management.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getStorage } from '@/lib/state/ssr-storage';
import { supabase, isSupabaseConfigured } from '../supabase/client';
import { reportError } from '@/lib/errors';


import type {
  Subscription,
  SubscriptionTier,
  SubscriptionStatus,
  StartupTier,
  InvestorTier,
  BillingInterval,
} from '../subscription/types';
import {
  getDefaultTier,
  isSubscriptionActive,
  needsAttention,
} from '../subscription/types';
import type {
  FeatureKey,
  StartupFeatureKey,
  InvestorFeatureKey,
} from '../subscription/features';
import {
  startupHasFeature,
  investorHasFeature,
  getStartupFeatureLimit,
  getInvestorFeatureLimit,
  STARTUP_FEATURES,
  INVESTOR_FEATURES,
} from '../subscription/features';

// ============================================================================
// Constants
// ============================================================================

/**
 * Tier assumed while a subscription is still loading (P0-4).
 *
 * During the async login window the store holds { subscription: null,
 * isLoading: true } — indistinguishable from a genuinely free user unless we
 * consult isLoading. We resolve that ambiguity OPTIMISTICALLY: assume the
 * highest tier so every gate passes and a paying customer is never flashed the
 * paywall mid-load. The moment isLoading flips to false the real resolved tier
 * (free, if there is no row / the load failed) governs again, so genuinely-free
 * users are correctly gated once load settles.
 *
 * Rationale (asymmetric risk): a false-negative — blocking a paying customer —
 * is far worse than a false-positive — a free user briefly seeing a feature
 * that then locks. The window is the network round-trip only (~50–500ms).
 */
const OPTIMISTIC_LOADING_TIER: SubscriptionTier = 'premium';

// ============================================================================
// Store Types
// ============================================================================

interface SubscriptionState {
  // Subscription data
  subscription: Subscription | null;
  stripeCustomerId: string | null;

  // User role (synced from inverio-store)
  userRole: 'startup' | 'investor' | 'admin' | null;

  // Lifecycle tracking
  gracePeriodEnd: string | null;
  lastPaymentDate: string | null;
  nextPaymentDate: string | null;
  paymentRetryCount: number;

  // Computed tier getters (for role-specific access)
  getStartupTier: () => StartupTier | null;
  getInvestorTier: () => InvestorTier | null;

  // Feature access methods
  hasFeature: (feature: FeatureKey) => boolean;
  getFeatureLimit: (feature: FeatureKey) => number;
  getCurrentTier: () => SubscriptionTier;

  // Status helpers
  isActive: () => boolean;
  isPastDue: () => boolean;
  isCanceled: () => boolean;
  isTrialing: () => boolean;

  // Actions
  setSubscription: (subscription: Subscription | null) => void;
  setUserRole: (role: 'startup' | 'investor' | 'admin' | null) => void;
  setStripeCustomerId: (customerId: string | null) => void;

  // Mock subscription management (for development)
  mockSubscribe: (
    tier: SubscriptionTier,
    interval: BillingInterval
  ) => Promise<void>;
  mockCancelSubscription: () => Promise<void>;
  mockUpgrade: (newTier: SubscriptionTier) => Promise<void>;
  mockDowngrade: (newTier: SubscriptionTier) => Promise<void>;
  mockSetStatus: (status: SubscriptionStatus) => void;

  // Lifecycle
  setGracePeriodEnd: (date: string | null) => void;
  incrementRetryCount: () => void;
  resetRetryCount: () => void;

  // Load from database
  loadSubscription: () => Promise<void>;
  isLoading: boolean;

  // Reset
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  subscription: null as Subscription | null,
  stripeCustomerId: null as string | null,
  userRole: null as 'startup' | 'investor' | 'admin' | null,
  gracePeriodEnd: null as string | null,
  lastPaymentDate: null as string | null,
  nextPaymentDate: null as string | null,
  paymentRetryCount: 0,
  isLoading: false,
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ========================================================================
      // Computed Tier Getters
      // ========================================================================

      getStartupTier: (): StartupTier | null => {
        const { subscription, userRole, isLoading } = get();
        if (userRole !== 'startup') return null;

        if (subscription && isSubscriptionActive(subscription.status)) {
          return subscription.tier as StartupTier;
        }
        // P0-4: while the subscription is still loading we cannot tell "free"
        // from "not yet known". Stay optimistic (highest tier) so a paying user
        // is never gated during the async login window. Once isLoading flips to
        // false the real (default/free) tier applies. See OPTIMISTIC_LOADING_TIER.
        if (isLoading && !subscription) {
          return OPTIMISTIC_LOADING_TIER as StartupTier;
        }
        return getDefaultTier('startup') as StartupTier;
      },

      getInvestorTier: (): InvestorTier | null => {
        const { subscription, userRole, isLoading } = get();
        if (userRole !== 'investor') return null;

        if (subscription && isSubscriptionActive(subscription.status)) {
          return subscription.tier as InvestorTier;
        }
        // P0-4: optimistic during load — see getStartupTier / OPTIMISTIC_LOADING_TIER.
        if (isLoading && !subscription) {
          return OPTIMISTIC_LOADING_TIER as InvestorTier;
        }
        return getDefaultTier('investor') as InvestorTier;
      },

      // ========================================================================
      // Feature Access Methods
      // ========================================================================

      hasFeature: (feature: FeatureKey): boolean => {
        const { subscription, userRole, isLoading } = get();

        // Admin users bypass all feature gates
        if (userRole === 'admin') return true;

        // P0-4: don't gate while the subscription is still loading. A
        // false-negative (blocking a paying customer during the async login
        // window) is far worse than a false-positive (a free user briefly
        // seeing a feature that then locks once load settles). We can only be
        // optimistic for a known role; with no role yet there is nothing to
        // unlock against, so fall through to the normal (denied) path.
        if (isLoading && !subscription && userRole) {
          return true;
        }

        // Determine current tier
        let tier: SubscriptionTier;
        if (subscription && isSubscriptionActive(subscription.status)) {
          tier = subscription.tier;
        } else {
          // No active subscription - use default tier
          tier = userRole ? getDefaultTier(userRole as 'startup' | 'investor') : 'free';
        }

        // Check feature access based on user role
        if (userRole === 'startup') {
          return startupHasFeature(
            tier as StartupTier,
            feature as StartupFeatureKey
          );
        } else if (userRole === 'investor') {
          return investorHasFeature(
            tier as InvestorTier,
            feature as InvestorFeatureKey
          );
        }

        return false;
      },

      getFeatureLimit: (feature: FeatureKey): number => {
        const { subscription, userRole, isLoading } = get();

        // Admin users have unlimited access
        if (userRole === 'admin') return Infinity;

        // Determine current tier
        let tier: SubscriptionTier;
        if (subscription && isSubscriptionActive(subscription.status)) {
          tier = subscription.tier;
        } else if (isLoading && !subscription && userRole) {
          // P0-4: optimistic while loading so a paying user is never shown a
          // free-tier limit (e.g. "0 messages") mid-load. See hasFeature.
          tier = OPTIMISTIC_LOADING_TIER;
        } else {
          tier = userRole ? getDefaultTier(userRole as 'startup' | 'investor') : 'free';
        }

        if (userRole === 'startup') {
          return getStartupFeatureLimit(
            tier as StartupTier,
            feature as StartupFeatureKey
          );
        }

        if (userRole === 'investor') {
          return getInvestorFeatureLimit(
            tier as InvestorTier,
            feature as InvestorFeatureKey
          );
        }

        return get().hasFeature(feature) ? 1 : 0;
      },

      getCurrentTier: (): SubscriptionTier => {
        const { subscription, userRole, isLoading } = get();

        if (subscription && isSubscriptionActive(subscription.status)) {
          return subscription.tier;
        }

        // P0-4: optimistic while loading (see hasFeature / OPTIMISTIC_LOADING_TIER).
        if (isLoading && !subscription && userRole && userRole !== 'admin') {
          return OPTIMISTIC_LOADING_TIER;
        }

        if (userRole && userRole !== 'admin') {
          return getDefaultTier(userRole);
        }
        return 'free';
      },

      // ========================================================================
      // Status Helpers
      // ========================================================================

      isActive: (): boolean => {
        const { subscription } = get();
        return subscription ? isSubscriptionActive(subscription.status) : false;
      },

      isPastDue: (): boolean => {
        const { subscription } = get();
        return subscription?.status === 'past_due';
      },

      isCanceled: (): boolean => {
        const { subscription } = get();
        return subscription?.status === 'canceled';
      },

      isTrialing: (): boolean => {
        const { subscription } = get();
        return subscription?.status === 'trialing';
      },

      // ========================================================================
      // Setters
      // ========================================================================

      setSubscription: (subscription) => set({ subscription }),

      setUserRole: (userRole) => {
        set({ userRole });
      },

      setStripeCustomerId: (stripeCustomerId) => set({ stripeCustomerId }),

      setGracePeriodEnd: (gracePeriodEnd) => set({ gracePeriodEnd }),

      incrementRetryCount: () =>
        set((state) => ({ paymentRetryCount: state.paymentRetryCount + 1 })),

      resetRetryCount: () => set({ paymentRetryCount: 0 }),

      // ========================================================================
      // Mock Subscription Management
      // ========================================================================

      mockSubscribe: async (
        tier: SubscriptionTier,
        interval: BillingInterval
      ): Promise<void> => {
        // Simulate API delay
        await new Promise((resolve) => setTimeout(resolve, 500));

        const now = new Date();
        const periodEnd = new Date(now);
        if (interval === 'month') {
          periodEnd.setMonth(periodEnd.getMonth() + 1);
        } else {
          periodEnd.setFullYear(periodEnd.getFullYear() + 1);
        }

        const subscription: Subscription = {
          id: `sub_mock_${Date.now()}`,
          tier,
          status: 'active',
          billingInterval: interval,
          currentPeriodStart: now.toISOString(),
          currentPeriodEnd: periodEnd.toISOString(),
          cancelAtPeriodEnd: false,
          stripeSubscriptionId: `stripe_sub_mock_${Date.now()}`,
          stripeCustomerId: get().stripeCustomerId || `cus_mock_${Date.now()}`,
        };

        set({
          subscription,
          lastPaymentDate: now.toISOString(),
          nextPaymentDate: periodEnd.toISOString(),
          paymentRetryCount: 0,
          gracePeriodEnd: null,
        });
      },

      mockCancelSubscription: async (): Promise<void> => {
        await new Promise((resolve) => setTimeout(resolve, 300));

        const { subscription } = get();
        if (!subscription) return;

        set({
          subscription: {
            ...subscription,
            cancelAtPeriodEnd: true,
            canceledAt: new Date().toISOString(),
          },
        });
      },

      mockUpgrade: async (newTier: SubscriptionTier): Promise<void> => {
        await new Promise((resolve) => setTimeout(resolve, 500));

        const { subscription } = get();
        if (!subscription) {
          // If no subscription, create new one
          await get().mockSubscribe(newTier, 'month');
          return;
        }

        // Upgrade existing subscription
        set({
          subscription: {
            ...subscription,
            tier: newTier,
            cancelAtPeriodEnd: false,
            canceledAt: undefined,
          },
        });
      },

      mockDowngrade: async (newTier: SubscriptionTier): Promise<void> => {
        await new Promise((resolve) => setTimeout(resolve, 500));

        const { subscription } = get();
        if (!subscription) return;

        // Downgrade takes effect at end of current period
        // For now, we'll apply it immediately in mock
        set({
          subscription: {
            ...subscription,
            tier: newTier,
          },
        });
      },

      mockSetStatus: (status: SubscriptionStatus): void => {
        const { subscription } = get();
        if (!subscription) return;

        let gracePeriodEnd: string | null = null;
        if (status === 'past_due') {
          // Set 7-day grace period
          const gracePeriod = new Date();
          gracePeriod.setDate(gracePeriod.getDate() + 7);
          gracePeriodEnd = gracePeriod.toISOString();
        }

        set({
          subscription: {
            ...subscription,
            status,
          },
          gracePeriodEnd,
        });
      },

      // ========================================================================
      // Load from Database
      // ========================================================================

      loadSubscription: async (): Promise<void> => {
        if (!isSupabaseConfigured()) {
          return;
        }

        set({ isLoading: true });

        try {
          // Get current user
          const { data: { user }, error: userError } = await supabase.auth.getUser();

          if (!user) {
            set({ isLoading: false });
            return;
          }

          // Fetch subscription from database
          // JUSTIFIED as any: 'subscriptions' table not in generated Supabase types yet
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data, error } = await supabase
            .from('subscriptions' as any)
            .select('*')
            .eq('user_id', user.id)
            .single<{
              id: string;
              user_id: string;
              tier: string;
              status: string;
              billing_interval: string | null;
              stripe_customer_id: string | null;
              stripe_subscription_id: string | null;
              current_period_start: string | null;
              current_period_end: string | null;
              cancel_at_period_end: boolean;
              canceled_at: string | null;
              trial_end: string | null;
              grace_period_end: string | null;
              created_at: string;
              updated_at: string;
            }>();

          if (error) {
            if (error.code === 'PGRST116') {
              // No subscription found - user is on free tier
              set({ subscription: null, isLoading: false });
              return;
            }
            reportError(error, { context: 'SubscriptionStore.loadSubscription.query', level: 'warning' });
            set({ isLoading: false });
            return;
          }

          if (data) {
            try {
              // Map database record to Subscription type
              const subscription: Subscription = {
                id: data.id,
                tier: data.tier as SubscriptionTier,
                status: data.status as SubscriptionStatus,
                billingInterval: (data.billing_interval || 'month') as BillingInterval,
                currentPeriodStart: data.current_period_start || '',
                currentPeriodEnd: data.current_period_end || '',
                cancelAtPeriodEnd: data.cancel_at_period_end || false,
                canceledAt: data.canceled_at || undefined,
                trialEnd: data.trial_end || undefined,
                stripeSubscriptionId: data.stripe_subscription_id || undefined,
                stripeCustomerId: data.stripe_customer_id || undefined,
              };

              // Migration guard: remap removed 'enterprise' tier to 'premium'
              if (subscription.tier === ('enterprise' as SubscriptionTier)) {
                subscription.tier = 'premium';
              }

              set({
                subscription,
                stripeCustomerId: data.stripe_customer_id,
                gracePeriodEnd: data.grace_period_end,
                nextPaymentDate: data.current_period_end,
                isLoading: false,
              });

            } catch (mapErr) {
              reportError(mapErr, { context: 'SubscriptionStore.loadSubscription.map', level: 'warning' });
              set({ isLoading: false });
            }
          }
        } catch (err) {
          reportError(err, { context: 'SubscriptionStore.loadSubscription' });
          set({ isLoading: false });
        }
      },

      // ========================================================================
      // Reset
      // ========================================================================

      reset: () => set(initialState),
    }),
    {
      name: 'inverio-subscription',
      storage: createJSONStorage(getStorage),
      partialize: (state) => ({
        subscription: state.subscription,
        stripeCustomerId: state.stripeCustomerId,
        userRole: state.userRole,
        gracePeriodEnd: state.gracePeriodEnd,
        lastPaymentDate: state.lastPaymentDate,
        nextPaymentDate: state.nextPaymentDate,
      }),
    }
  )
);

// ============================================================================
// Selector Hooks
// ============================================================================

/**
 * Hook to check if a specific feature is available
 */
export function useHasFeature(feature: FeatureKey): boolean {
  return useSubscriptionStore((s) => s.hasFeature(feature));
}

/**
 * Hook to get the current subscription tier
 */
export function useCurrentTier(): SubscriptionTier {
  const subscriptionStatus = useSubscriptionStore((s) => s.subscription?.status ?? null);
  const subscriptionTier = useSubscriptionStore((s) => s.subscription?.tier ?? null);
  const userRole = useSubscriptionStore((s) => s.userRole);
  const isLoading = useSubscriptionStore((s) => s.isLoading);

  if (subscriptionStatus && subscriptionTier && isSubscriptionActive(subscriptionStatus)) {
    return subscriptionTier;
  }

  // P0-4: optimistic while loading — mirrors getCurrentTier so the reactive
  // hook and the imperative getter never disagree during the login window.
  if (isLoading && !subscriptionTier && userRole && userRole !== 'admin') {
    return OPTIMISTIC_LOADING_TIER;
  }

  if (userRole && userRole !== 'admin') {
    return getDefaultTier(userRole);
  }
  return 'free';
}

/**
 * Hook to get subscription status
 */
export function useSubscriptionStatus(): SubscriptionStatus {
  return useSubscriptionStore((s) => s.subscription?.status ?? 'none');
}

/**
 * Hook to check if subscription needs attention (past_due, etc.)
 */
export function useNeedsAttention(): boolean {
  return useSubscriptionStore((s) =>
    s.subscription ? needsAttention(s.subscription.status) : false
  );
}
